/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useUserContext, useThemeContext } from 'contexts';
import TextField from 'components/control/text-field';
import TextArea from 'components/control/text-area';
import Checkbox from 'components/control/checkbox';
import Button from 'components/control/button';
import { AnalyticsController } from 'analytics/analytics-controller';
import PlusIcon from 'components/icon/plus-icon';
import { buildClassName } from 'utils/build-class-name';
import SuccessMessage from './success-message';
import styles from './style.module.scss';
import SignUpCta from '../sign-up-cta';
import { preconIds, listingPageModalIds, loginRegistrationIds } from 'constants/test-constants';
import { areDisclaimersChecked } from 'utils/consent';
import { useConsentService } from 'hooks/use-consent-service';
import PhoneCTA from './call-cta';
import Snackbar from 'components/snackbar';
import { MLML_AGENT_COOKIE_NAME } from 'constants/cookies';
import Cookies from 'js-cookie';
import {
  GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA,
  GTM_CLICK_ADDRESS_PAGE_PHOTO_GALLERY_PVR_CTA,
  GTM_PROHIBITED_EMAILS,
} from 'constants/events';
import { createConnectionRequest } from 'data/connection-request';
import ListingAgentInPvr from './listing-agent';
import FormMessages from 'components/form/messages';
import { useLocale } from 'hooks/use-locale';
import { getTenantById, isExpTenant, themeNameToTenantId } from '@tenants';
import { useTranslation } from 'next-i18next';
import { LOCALE_CONNECTION_REQUEST_PATHS, Namespaces } from 'types/locales';
import { isComingSoon } from 'types/reso';
import { LeadObservabilityService } from 'observability/services/lead-observability';

import type Listing from 'data/listing';
import type { ListingAgentInfo } from 'components/pages/address/public/components/listing-agent';

export type ListingInfoType = Pick<Listing, 'isAvailable' | 'streetAddress' | 'unitNumber' | 'city' | 'province' | 'id' | 'isOffMarket' | 'isRental' | 'addressPathWithFallback' | 'expAgentId' | 'addressSlug' | 'standardStatus'>;

export interface ConnectionRequestProps {
  /** The object containing information about the property. */
  listing: ListingInfoType;

  /** Determines whether the component is being displayed in a modal. Default is *false* */
  isInModal?: boolean;

  /**  Determines whether to display the 'Take a Tour' or 'Request Info' button label. Default is *true*  */
  isViewingRequest?: boolean;

  /**  Determines if the component is on an address page. Default is *false*  */
  isAddressPage?: boolean;

  /**  Determines if the component is on the photo gallery. Default is *false*  */
  isOnPhotoGallery?: boolean;

  /**  Determines if the component should provide agent info. Default is *false*  */
  agentInfo?: ListingAgentInfo;

  /**  Custom button text to display. Default is *undefined*  */
  customButtonText?: string;

  /** Optional agent card rendered inside the form header, replacing the default copy text. */
  agentCardSlot?: React.ReactNode;


  prefillData?: {
    requestViewing?: boolean;
    isShowingAdditionalComments?: boolean;
    sourceComments?: string;
  };
}

export default function ConnectionRequest({ listing, isInModal, isViewingRequest = true, isAddressPage = false, isOnPhotoGallery = false, agentInfo, customButtonText, agentCardSlot, prefillData = {}}: ConnectionRequestProps) {
  const { t } = useTranslation(Namespaces.CONNECTION_REQUEST);
  const { themeName } = useThemeContext();
  const tenant = getTenantById(themeNameToTenantId(themeName));

  const isComingSoonListing = !!listing.standardStatus && isComingSoon(listing.standardStatus);

  const { isAuthenticated, user, saveAndPersistUser, userLocation, hasAcceptedConnRequestDisclaimers, handleConnRequestDisclaimers } = useUserContext();
  const { register, handleSubmit, formState: { errors }, setError, clearErrors, reset, setValue } = useForm();
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [commentsPlaceholder, setCommentsPlaceholder] = useState(t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_PLACEHOLDER_MESSAGE, { defaultValue: 'Message' }));
  // Coming soon listings have no viewing checkbox, so the lead is treated as a contact/info request rather than a tour.
  const [requestViewing, setRequestViewing] = useState(isComingSoonListing ? false : (prefillData.requestViewing ?? isViewingRequest));
  const [termsPrivacy, setTermsPrivacy] = useState(hasAcceptedConnRequestDisclaimers);
  const [hasConsent, setHasConsent] = useState(hasAcceptedConnRequestDisclaimers);
  const [isShowingAdditionalComments, setIsShowingAdditionalComments] = useState(prefillData.isShowingAdditionalComments ?? isInModal);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  const [eventAction, setEventAction] = useState('');
  const [userActivity, setUserActivity] = useState<string[]>([]);
  const [agreeToNewsletter, setAgreeToNewsletter] = useState(false);
  const userActivityRef = useRef<string[]>();
  const successfullySubmittedRef = useRef(false);
  const tenantPhoneNumber = tenant?.config?.organization?.phoneNumber() || '1-844-683-4664'; //FIXME
  const { isAvailable, streetAddress, id: listingId, isOffMarket, isRental, addressPathWithFallback } = listing;
  const { currentLocale } = useLocale(tenant.id);

  const isExpTheme = isExpTenant(tenant.id);
  const showsMLMLCreate = tenant.features.MyLinkMyLead && !!Cookies.get(MLML_AGENT_COOKIE_NAME);
  const showsMLML = tenant.features.MyLinkMyLead && !showsMLMLCreate;
  const takeTour = t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_BUTTON_TAKE_TOUR, { defaultValue: 'Take a Tour' });
  const getButtonText = () => {
    // Explicit override from the caller always wins.
    if (customButtonText) {
      return customButtonText;
    }
    if (isComingSoonListing) {
      return t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_BUTTON_CONTACT_AGENT, { defaultValue: 'Contact Agent' });
    }
    if (isOffMarket) {
      return t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_BUTTON_FREE_CONSULTATION, { defaultValue: 'Free Consultation' });
    }
    if (requestViewing) {
      return takeTour;
    }
    return t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_BUTTON_REQUEST_INFO, { defaultValue: 'Request Info' });
  };
  const buttonText = getButtonText();
  const userHasPhoneNumber = user && user.homePhone;

  // Consent service - handles all consent logic
  const { service: consentService, labels } = useConsentService(buttonText);
  const hasDisclaimerErrors = consentService.isRequired && (!!errors.termsPrivacy || !!errors.hasConsent);

  const getFormTitle = () => {
    if (isOffMarket) {
      return t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_TITLE_OFF_MARKET, { defaultValue: 'What\'s this home worth?' });
    } else if (agentInfo) {
      return t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_TITLE_CONTACT_AGENT, { defaultValue: 'Contact Agent' });
    } else {
      return t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_TITLE_LIKE_WHAT_YOU_SEE, { defaultValue: 'Like what you see?' });
    }
  };

  const getCopyLanguage = () => {
    if (isOffMarket) {
      return (
        <p className={`${styles.note} ${styles.doubleLineHeight}`}>
          {t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_COPY_OFF_MARKET, { defaultValue: 'Connect with a local agent for a free home estimate and information about sales in the area.' })}
        </p>
      );
    } else if (agentInfo) {
      return (
        <ListingAgentInPvr agentInfo={agentInfo} />
      );
    } else {
      return (
        <p className={`${styles.note} ${styles.doubleLineHeight}`}>
          {t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_COPY_DEFAULT, { defaultValue: 'Complete the form and we\'ll be in touch.' })} {!isAuthenticated ? t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_COPY_NO_LOGIN, { defaultValue: 'No login necessary.' }) : ''}
        </p>
      );
    }
  };

  const { privacyPolicy: privacyPolicyConsentLabel, marketingCommunications: marketingCommunicationsConsentLabel } = labels;

  const sendRequest = async (values: Record<string, unknown>) => {
    if (!areDisclaimersChecked({ isVisibleDisclaimerTCPA: consentService.isRequired, termsPrivacy, hasConsent, setError, handleConnRequestDisclaimers })) {
      const consentErrorMessage = errors.termsPrivacy?.message || errors.hasConsent?.message || 'Not checked';
      LeadObservabilityService.trackRequestFailure({
        pvrData: {
          leadType: requestViewing ? 'tour' : 'info',
          listingId: listingId ? String(listingId) : undefined,
          email: (user?.email || values.email as string) || undefined,
          isAuthenticated,
        },
        errorCategory: 'consent_not_given',
        errorMessage: consentErrorMessage as string,
      });
      return;
    }

    const checkFormValues = () => {
      // Check for missing required fields
      if (!values.firstName || !values.lastName || !values.email) {
        setFormErrorMessage(t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_ERROR_FILL_REQUIRED_FIELDS, { defaultValue: 'Please fill in all required fields.' }));
        return false; // validation failed
      }

      // Check for phone number specifically
      const phoneValue = values.phone as string;
      if (!phoneValue || phoneValue.trim() === '') {
        setFormErrorMessage(t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_ERROR_FILL_REQUIRED_FIELDS, { defaultValue: 'Please fill in all required fields.' }));
        return false; // validation failed
      }

      return true; // validation passed
    };

    // Check form values and return early if validation fails
    if (!checkFormValues()) {
      const missingFieldsMessage = t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_ERROR_FILL_REQUIRED_FIELDS, { defaultValue: 'Please fill in all required fields.' });
      LeadObservabilityService.trackRequestFailure({
        pvrData: {
          leadType: requestViewing ? 'tour' : 'info',
          listingId: listingId ? String(listingId) : undefined,
          email: (user?.email || values.email as string) || undefined,
          isAuthenticated,
        },
        errorCategory: 'missing_fields',
        errorMessage: missingFieldsMessage,
      });
      return;
    }

    const trackAllEvents = () => {
      // Prevent prohibited test automation emails from sending PVR events
      const isProhibitedEmail = GTM_PROHIBITED_EMAILS.includes(values.email as string);
      if (isProhibitedEmail) return;
      if (isAddressPage) {
        if (isOnPhotoGallery) {
          AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_PHOTO_GALLERY_PVR_CTA);
        }
        else {
          AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA);
        }
        if (!isInModal && buttonText == takeTour) AnalyticsController.trackEvent('XpA_AddressTakeATourButClk');
      }
      AnalyticsController.trackEvent(eventAction);
      if (isInModal) {
        AnalyticsController.trackEvent('Mobile_PVR_Submit');
      }
      if (showsMLML) {
        AnalyticsController.trackEvent('MLML_Lead');
      }
      AnalyticsController.trackEvent('connection-request-submission'); // Temporarily added for Smart Chat Experiment
      AnalyticsController.trackEvent(isInModal ? 'mobile-submit-pvr' : 'desktop-submit-pvr');
    };

    const sourceComments = listing && !isAvailable ? 'How much did this property sell for?' : values.sourceComments;
    const properties = {
      ...values,
      listingId,
      streetAddress,
      requestViewing,
      sourceComments,
      newsletter: agreeToNewsletter,
      userCountry: userLocation?.countryCode,
      theme: themeName,
      addressPath: addressPathWithFallback,
      locale: user?.locale || currentLocale,
    };

    createConnectionRequest(properties)
      .then(async () => {
        setSuccessfullySubmitted(true);
        AnalyticsController.trackConnectionRequest();
        reset();
        trackAllEvents();
        if (agreeToNewsletter) {
          AnalyticsController.trackEvent(`newsletter-signup-${eventAction}`);
          if (user) {
            saveAndPersistUser({ ...user, newsletter: true });
          }
        }
        await consentService.capture({
          firstName: user?.firstName || values.firstName as string,
          lastName: user?.lastName || values.lastName as string,
          email: user?.email || values.email as string,
          phone: user?.homePhone || values.phone as string,
          listingId: listingId,
          listingUrl: addressPathWithFallback,
        });
      }).catch(({ errors: serverErrors }) => {
        let formErrorMessage = t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_ERROR_SOMETHING_WRONG, { defaultValue: 'Something went wrong' });
        let errorCategory: 'invalid_data' | 'api_error' = 'api_error';

        serverErrors.forEach(({ attribute, _message }) => {
          if (attribute === 'email') {
            formErrorMessage = t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_ERROR_INVALID_EMAIL, { defaultValue: 'Invalid email' });
            errorCategory = 'invalid_data';
          } else if (attribute === 'homePhone') {
            formErrorMessage = t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_ERROR_INVALID_PHONE, { defaultValue: 'Invalid phone number' });
            errorCategory = 'invalid_data';
          } else {
            formErrorMessage = t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_ERROR_SOMETHING_WRONG, { defaultValue: 'Something went wrong' });
          }
        });

        setFormErrorMessage(formErrorMessage);

        LeadObservabilityService.trackRequestFailure({
          pvrData: {
            leadType: requestViewing ? 'tour' : 'info',
            listingId: listingId ? String(listingId) : undefined,
            email: (user?.email || values.email as string) || undefined,
            isAuthenticated,
          },
          errorCategory,
          errorMessage: formErrorMessage,
        });
      });
  };

  const requestViewingOfActiveListing = listing && isAvailable && requestViewing;

  const getRequestViewingLabel = () => {
    return isAvailable ? t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_CHECKBOX_VIEW_PROPERTY, { defaultValue: 'I want to view this property' }) : t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_CHECKBOX_VIEW_SIMILAR, { defaultValue: 'I want to view properties like this one' });
  };

  const generateCommentsPlaceholder = () => {
    let commentsPlaceholder = '';
    if (listing) {
      if (requestViewingOfActiveListing) {
        commentsPlaceholder = t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_PLACEHOLDER_COMMENTS_VIEWING, { defaultValue: 'When would you like to see this property?' });
      } else {
        commentsPlaceholder = t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_PLACEHOLDER_COMMENTS_INFO, { defaultValue: 'What would you like to know about this property?' });
      }
    }
    setCommentsPlaceholder(commentsPlaceholder);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing derived-state effect, unrelated to this change
    generateCommentsPlaceholder();
    if (prefillData.sourceComments) {
      setValue('sourceComments', prefillData.sourceComments); // Set the prefill value and make the field editable
    }
  }, [listing, requestViewingOfActiveListing, prefillData.sourceComments]);

  useEffect(() => {
    let eventAction = `property_${requestViewing ? 'viewing': 'info'}_request`;
    if (isRental) {
      eventAction += '_rent';
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing derived-state effect, unrelated to this change
    setEventAction(eventAction);
  }, [requestViewing, listing]);

  const captureFormEvents = (event: React.FormEvent<HTMLFormElement>) => {
    event.persist();
    const name = (event.target as HTMLInputElement).name;
    if (name && name !== 'requestViewing') {
      setUserActivity(prev => [...prev, name]);
    }
  };

  const getFilteredActivityList = () => {
    if (userActivityRef.current) {
      return userActivityRef.current.reduce((accum: string[], activity: string) => {
        if (accum[accum.length - 1] !== activity) {
          accum.push(activity);
        }
        return accum;
      }, []);
    }
  };

  useEffect(() => {
    return () => {
      const activity = getFilteredActivityList()?.join(' > ');
      if (!user && activity && !successfullySubmittedRef.current) {
        // Track drop off point of users abandoning form during filling the form out
        AnalyticsController.trackEvent(`activity: ${activity}`, 'connectionRequestFormAbandonment');
      } else if (!user && isInModal && !activity && !successfullySubmittedRef.current) {
        // Track a user not interacting with PVR and closing it on mobile
        AnalyticsController.trackEvent('abandon-connection-request-modal', 'connectionRequestFormAbandonment');
      }
    };
  }, []);

  // Used to access state during component unmounting
  useEffect(() => {
    successfullySubmittedRef.current = successfullySubmitted;
  }, [successfullySubmitted]);

  useEffect(() => {
    userActivityRef.current = userActivity;
  }, [userActivity]);

  return (
    <>
      <form
        onChange={captureFormEvents}
        onSubmit={e =>{
          clearErrors(); // clear manually before submit as we use custom error messages
          handleSubmit(sendRequest)(e);
        }}
        className={buildClassName(styles.component, isInModal && styles.modal, isExpTheme && styles['exp-theme'])}
        id={eventAction}
        data-testid={listingPageModalIds.connectionRequestForm}
      >
        {!successfullySubmitted ?
          <>
            <h3
              data-testid={listingPageModalIds.connectionRequestFormTitle}
            >
              {getFormTitle()}
            </h3>
            {getCopyLanguage()}
            <FormMessages errors={formErrorMessage} isLargeError key={formErrorMessage} />
            <TextField placeholder={t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_PLACEHOLDER_FIRST_NAME, { defaultValue: 'First Name' })} {...register('firstName', { value: user?.firstName })} value={user?.firstName} isHidden={isAuthenticated && !errors.firstName} testId={listingPageModalIds.requestfirstname} />
            <TextField placeholder={t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_PLACEHOLDER_LAST_NAME, { defaultValue: 'Last Name' })} {...register('lastName', { value: user?.lastName })} testId={listingPageModalIds.requestlastname} value={user?.lastName} isHidden={isAuthenticated && !errors.lastName} />
            <TextField placeholder={t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_PLACEHOLDER_EMAIL, { defaultValue: 'Email' })} {...register('email', { value: user?.email })} testId={listingPageModalIds.requestemail} value={user?.email} />
            <TextField placeholder={t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_PLACEHOLDER_PHONE, { defaultValue: 'Phone Number' })} {...register('phone', { value: user?.homePhone })} testId={listingPageModalIds.requestphonenumber} value={user?.homePhone} isHidden={isAuthenticated && userHasPhoneNumber && !formErrorMessage} />
            {!isShowingAdditionalComments && <div onClick={() => setIsShowingAdditionalComments(true)} className={styles['add-comment']} data-testid={listingPageModalIds.requestAddComment}><PlusIcon /> {t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_ADD_COMMENT, { defaultValue: 'Add a Comment' })}</div>}
            {isShowingAdditionalComments &&
              <div className={styles['comment-box']}>
                <TextArea
                  placeholder={commentsPlaceholder}
                  {...register('sourceComments')}
                  testId={listingPageModalIds.requestComments}
                  defaultValue={prefillData.sourceComments || ''}
                />
              </div>
            }
            {listing && !isOffMarket && !isComingSoonListing &&
              <div className={styles['checkbox-container']}>
                <Checkbox
                  label={getRequestViewingLabel()}
                  name="requestViewing"
                  value={requestViewing}
                  onValueChange={setRequestViewing}
                  checked={requestViewing}
                  testId={listingPageModalIds.requestViewingCheckbox}
                />
              </div>
            }
            {(!user?.newsletter && !isExpTheme) &&
              <div className={styles['checkbox-container']}>
                <Checkbox
                  label={<span className={styles.doubleLineHeight}>{t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_CHECKBOX_NEWSLETTER, { defaultValue: 'Yes, I\'d like to receive free weekly market insights and buying and selling tips. Unsubscribe at any time.' })}</span>}
                  name='agreeToNewsletter'
                  value={agreeToNewsletter}
                  onValueChange={setAgreeToNewsletter}
                  testId={loginRegistrationIds.createAccountNewslettersCheckbox}
                />
              </div>
            }
            {hasDisclaimerErrors && <p className={styles['disclaimer-error']}>{t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_DISCLAIMER_ERROR, { phoneNumber: tenantPhoneNumber, defaultValue: 'Please check the boxes below to continue, or call us at {{phoneNumber}}' })}</p>}
            <div className={buildClassName(styles['submit-button-container'], isExpTheme && styles['submit-button-exp'])}>
              <Button label={buttonText} type="submit" theme={customButtonText == 'Learn More' ? 'secondary' : 'primary'} testId={preconIds.preconSubmitRequestButton} />
            </div>
            {consentService.isRequired && (
              <div className={styles.disclaimers}>
                <div className={styles['checkbox-container']}>
                  <Checkbox
                    label={privacyPolicyConsentLabel}
                    name="termsPrivacy"
                    value={termsPrivacy}
                    onValueChange={setTermsPrivacy}
                    hasError={!!errors.termsPrivacy}
                    testId={loginRegistrationIds.privacyPolicyConsentCheckbox}
                  />
                </div>
                <div className={styles['checkbox-container']}>
                  <Checkbox
                    label={marketingCommunicationsConsentLabel}
                    name="hasConsent"
                    value={hasConsent}
                    onValueChange={setHasConsent}
                    hasError={!!errors.hasConsent}
                    testId={loginRegistrationIds.marketingCommunicationsConsentCheckbox}
                  />
                </div>
              </div>
            )}
          </>
          :
          <SuccessMessage isInModal={isInModal} isAuthenticated={isAuthenticated} listing={listing} phoneNumber={tenant.pvr.phoneNumber} />
        }
        {!isInModal && !consentService.isRequired && !successfullySubmitted && <PhoneCTA phoneNumber={tenant.pvr.phoneNumber} />}
        {agentCardSlot && (
          <div className={styles['agent-card-footer']}>{agentCardSlot}</div>
        )}
      </form>
      {successfullySubmitted && !isAuthenticated && <SignUpCta />}
      <Snackbar />
    </>
  );
}
